"use client"

import {
  CalendarBlank,
  CaretDown,
  CaretUp,
  ChartBar,
  Database,
  Export,
  Gear,
  Plus,
  Table,
  X,
} from "@phosphor-icons/react"
import { ChartWithConfigId } from "@tokenterminal/core/feature/chart/ChartWithConfigId"
import { ChartContextProvider } from "@tokenterminal/core/feature/chart/tt-ui-chart/ChartContext"
import { Button } from "@tokenterminal/core/ui/button/Button"
import { IconButton } from "@tokenterminal/core/ui/button/IconButton"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { useState } from "react"
import { twMerge } from "tailwind-merge"
import { ClientOnlySuspense } from "@/components/ClientOnlySuspense"
import { FADE_MASK_STYLE } from "@/utils/masonryMask"

// Chart config ID for the demo
const CHART_CONFIG_ID = "ZDk4YTNhMjFhY2M4NmRkMDIxOGRhYjcx"

// Legend data matching the chart
const legendItems = [
  { name: "BNB Chain", color: "#1AFFAB" },
  { name: "Tron", color: "#4869EF" },
  { name: "Solana", color: "#483672" },
]

// Custom dropdown component
function DemoDropdown({
  label,
  value,
  className,
}: {
  label?: string
  value: string
  className?: string
}) {
  return (
    <div className={twMerge("flex flex-col gap-1.5", className)}>
      {label && <span className="text-xs text-fg-secondary">{label}</span>}
      <button className="flex items-center justify-between gap-2 px-3 py-2 bg-bg-surface border border-border-default rounded-lg text-sm text-fg-default hover:border-border-hover transition-colors">
        <span>{value}</span>
        <CaretDown className="w-4 h-4 text-fg-secondary" />
      </button>
    </div>
  )
}

// Custom input component
function DemoInput({
  label,
  value,
  className,
  icon,
}: {
  label?: string
  value: string
  className?: string
  icon?: React.ReactNode
}) {
  return (
    <div className={twMerge("flex flex-col gap-1.5", className)}>
      {label && (
        <div className="flex items-center gap-1">
          <span className="text-xs text-fg-secondary">{label}</span>
          {icon}
        </div>
      )}
      <div className="flex items-center gap-2 px-3 py-2 bg-bg-surface border border-border-default rounded-lg text-sm text-fg-default">
        <span>{value}</span>
      </div>
    </div>
  )
}

// Filter tag component
function FilterTag({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-stretch bg-bg-surface border border-border-default rounded-lg text-sm overflow-hidden">
      <span className="px-4 py-2.5 text-fg-secondary border-r border-border-default">{label}</span>
      <span className="px-3 py-2.5 text-fg-muted border-r border-border-default">is</span>
      <span className="px-4 py-2.5 text-fg-default font-medium">{value}</span>
      <button className="px-3 py-2.5 text-fg-secondary hover:text-fg-default transition-colors border-l border-border-default">
        <X className="w-4 h-4" />
      </button>
    </div>
  )
}

// Legend item component
function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-2 py-1">
      <div className="w-4 h-4 rounded" style={{ backgroundColor: color }} />
      <span className="text-sm text-fg-default">{label}</span>
    </div>
  )
}

// Collapsible section header
function SectionHeader({
  title,
  isExpanded,
  onToggle,
  rightSlot,
}: {
  title: string
  isExpanded: boolean
  onToggle: () => void
  rightSlot?: React.ReactNode
}) {
  return (
    <div className="flex items-center justify-between">
      <button
        className="flex items-center gap-2 text-fg-default hover:text-fg-secondary transition-colors"
        onClick={onToggle}
      >
        <Database className="w-4 h-4 text-fg-secondary" />
        <span className="text-sm font-medium">{title}</span>
      </button>
      <div className="flex items-center gap-1">
        {rightSlot}
        <IconButton
          variant="ghost"
          size="xsmall"
          icon={isExpanded ? CaretUp : CaretDown}
          label={isExpanded ? "Collapse" : "Expand"}
          onPress={onToggle}
        />
      </div>
    </div>
  )
}

function InteractiveChartEditorDemo() {
  const [isExpanded, setIsExpanded] = useState(true)

  return (
    <div
      className="relative border-t border-l border-solid border-border-default rounded-tl-xl"
      style={{
        overflow: "hidden",
        maxHeight: "700px",
        ...FADE_MASK_STYLE,
      }}
    >
      <div
        className="flex gap-0 rounded-2xl overflow-hidden bg-bg-default origin-top-left"
        style={{
          width: "calc(100% + 300px)",
          transform: "scale(0.90)",
          transformOrigin: "top left",
        }}
      >
        {/* Sidebar */}
        <div className="w-[375px] shrink-0 border-r border-border-default flex flex-col">
          {/* Series Header */}
          <div className="px-4 py-3 border-b border-border-default h-[48px] flex items-center">
            <SectionHeader
              title="Active users (daily)"
              isExpanded={isExpanded}
              onToggle={() => setIsExpanded(!isExpanded)}
            />
          </div>

          {isExpanded && (
            <div className="flex-1 overflow-y-auto">
              <div className="p-4 flex flex-col gap-5">
                {/* Metric */}
                <DemoDropdown label="Metric" value="Active users (daily)" />

                {/* Filters */}
                <div className="flex flex-col gap-3">
                  <span className="text-xs text-fg-secondary">Filters</span>
                  <FilterTag label="Market sector" value="Blockchains (L1)" />
                  <button className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm text-fg-secondary hover:text-fg-default border border-dashed border-border-default rounded-lg transition-colors self-start">
                    Add filter
                    <Plus className="w-4 h-4" />
                  </button>
                </div>

                {/* Group by & Limit */}
                <div className="flex gap-3">
                  <DemoDropdown label="Group by" value="Project" className="flex-1" />
                  <DemoInput label="Limit" value="5" className="w-20" />
                </div>

                <div className="h-px bg-border-default -mx-4" />

                {/* Chart type */}
                <DemoDropdown label="Chart type" value="Stacked bar" />

                {/* Display value as */}
                <DemoDropdown label="Display value as" value="Time series" />

                {/* Y-axis */}
                <div className="flex gap-3">
                  <DemoDropdown label="Y-axis" value="Left" className="w-24" />
                  <DemoInput label="Label" value="Active users (daily)" className="flex-1" />
                </div>

                <div className="h-px bg-border-default -mx-4" />

                {/* Legend */}
                <div className="flex flex-col gap-2">
                  <span className="text-xs text-fg-secondary">Legend</span>
                  <div className="flex flex-col gap-0.5">
                    {legendItems.slice(0, 3).map((item) => (
                      <LegendItem key={item.name} color={item.color} label={item.name} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Add metric button */}
          <div className="p-4 border-t border-border-default mt-auto">
            <Button variant="ghost" className="w-full opacity-65" isDisabled>
              <Icon as={Plus} />
              Add metric
            </Button>
          </div>
        </div>

        {/* Preview Panel */}
        <div className="flex flex-col min-w-0" style={{ width: "calc(100% - 375px + 200px)" }}>
          {/* Preview Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border-default h-[48px]">
            <span className="text-sm text-fg-secondary">Preview</span>
            <div className="flex items-center gap-2">
              <div className="flex items-center border border-border-default rounded-lg overflow-hidden">
                <button className="p-1.5 bg-bg-subtle border-r border-border-default">
                  <ChartBar className="w-4 h-4 text-fg-default" />
                </button>
                <button className="p-1.5 hover:bg-bg-subtle">
                  <Table className="w-4 h-4 text-fg-secondary" />
                </button>
              </div>
              <button className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-fg-default bg-bg-surface border border-border-default rounded-lg hover:border-border-hover">
                <CalendarBlank className="w-3.5 h-3.5" />
                Last 180 days
                <CaretDown className="w-3 h-3 text-fg-secondary" />
              </button>
              <button className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-fg-default bg-bg-surface border border-border-default rounded-lg hover:border-border-hover">
                Daily
                <CaretDown className="w-3 h-3 text-fg-secondary" />
              </button>
              <div className="flex items-center gap-1 ml-1">
                <IconButton variant="ghost" size="xsmall" icon={Gear} label="Settings" />
                <IconButton variant="ghost" size="xsmall" icon={Export} label="Export" />
              </div>
            </div>
          </div>

          {/* Chart Content */}
          <div className="flex-1 p-6 min-h-[500px]">
            <ClientOnlySuspense>
              <ChartContextProvider>
                <ChartWithConfigId
                  id={CHART_CONFIG_ID}
                  isVisible
                  showDescription
                  showTitle
                  disableClickToOpenModal
                />
              </ChartContextProvider>
            </ClientOnlySuspense>
          </div>
        </div>
      </div>
    </div>
  )
}

export default InteractiveChartEditorDemo
